<template>
  <div class="w-full">
    <!-- No weeks available message -->
    <div
      v-if="chartReady && weekRanges.length === 0 && !isLoadingWeeklyData"
      :style="{ width: '100%', height }"
      class="flex items-center justify-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"
    >
      <span class="text-sm">Geen beschikbaarheidsdata voor deze periode</span>
    </div>
    <VChart
      v-else-if="chartReady"
      ref="chartRef"
      :option="option"
      :notMerge="true"
      autoresize
      :style="{ width: '100%', height, cursor: 'pointer' }"
      @click="handleChartClick"
      @zr:click="handleZrClick"
    />
    <div
      v-else
      :style="{ width: '100%', height }"
      class="flex items-center justify-center text-gray-400 dark:text-gray-500"
    >
      Laden...
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkAreaComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { useBarChartData } from '@/composables/charts/useBarChartData.js'
import { useBarChartConfig } from '@/composables/charts/useBarChartConfig.js'

use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkAreaComponent,
  CanvasRenderer
])

const props = defineProps({
  employee: { type: Object, required: true },
  unit: { type: String, default: 'hours' },
  height: { type: String, default: '250px' },
  periodStart: { type: String, default: null },
  periodEnd: { type: String, default: null },
  labelColors: { type: Object, default: () => ({}) },
  potentialDpw: { type: Number, default: 0 },
  potentialLabel: { type: String, default: 'Huidige klus' },
  contractHoursPerWeek: { type: Number, default: 40 },
  targetBillability: { type: Number, default: 80 },
  showBillability: { type: Boolean, default: false },
  showCurrentWeekHighlight: { type: Boolean, default: false },
  // Extra proposed hours to add on top of server-side proposed_hours per week
  // (keyed by week_start). Used to visualize locally-staged proposals that
  // aren't persisted yet.
  extraProposedHoursByWeek: { type: Object, default: () => ({}) },
  // Preference whose proposed hours should be excluded from the proposed
  // series. Used on Concretiseren where the same role is already shown as the
  // grey potential bar (prevents double counting for the PL-selected employee).
  excludeProposedPreferenceId: { type: [Number, String], default: null }
})

const emit = defineEmits(['week-click'])

const chartRef = ref(null)

// 1. Config composable provides display detection + color mapping (no data dependency)
const { isMobile, getColorFor } = useBarChartConfig(props)

// 2. Data composable — fetches data, builds series using getColorFor and isMobile from config
const chartData = useBarChartData(props, getColorFor, isMobile)
const { weekRanges, isLoadingWeeklyData, chartReady } = chartData

// 3. Build ECharts option using chart data (option is a computed in the config composable)
const { option } = useBarChartConfig(props, chartData)

// --- Event handlers ---
const emitWeekClick = weekIndex => {
  const weekData = weekRanges.value[weekIndex]
  if (weekData) {
    emit('week-click', {
      index: weekIndex,
      week_start: weekData.start,
      week_end: weekData.end,
      iso_week: weekData.iso_week
    })
  }
}

const handleChartClick = params => {
  if (params.componentType === 'series' && params.dataIndex !== undefined) {
    emitWeekClick(params.dataIndex)
  }
}

const handleZrClick = params => {
  const chart = chartRef.value
  if (!chart) return

  const pointInPixel = [params.offsetX, params.offsetY]
  const pointInGrid = chart.convertFromPixel({ seriesIndex: 0 }, pointInPixel)

  if (pointInGrid) {
    const weekIndex = Math.round(pointInGrid[0])
    if (weekIndex >= 0 && weekIndex < weekRanges.value.length) {
      emitWeekClick(weekIndex)
    }
  }
}
</script>

<style scoped>
/* Ensure chart container allows tooltip overflow */
:deep(.echarts) {
  overflow: visible !important;
}
</style>
